import { useStore } from '@nanostores/react';
import { useCallback, useState } from 'react';
import { Dialog, DialogActions, DialogButton, DialogDescription, DialogTitle } from '~/components/ui/LegacyDialog';
import type { SliderTabDropdownItem } from '~/components/ui/SliderTabs';
import { SupabaseLogo } from '~/components/ui/logos/SupabaseLogo';
import { deleteSupabaseProjectIntegration } from '~/lib/.client/api/supabase';
import { projectStore } from '~/lib/stores/project';
import { supabaseStore } from '~/lib/stores/supabase';
import { userStore } from '~/lib/stores/user';
import { invariant } from '~/utils/invariant';

export function useSupabaseDropdownMenuItem(): {
  item: SliderTabDropdownItem<string> | null;
  dialog: JSX.Element | null;
} {
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const activeOrganization = useStore(userStore.getActiveOrganization());
  const showIntegrations = activeOrganization?.organizationSettings?.showIntegrations ?? true;
  const isSupabaseConfigured = useStore(supabaseStore.isConfigured);
  const isUsingSupabaseNative = useStore(supabaseStore.supabaseProjectIntegration)?.isBoltNative;
  const supabaseStatus = useStore(supabaseStore.supabaseStatus);
  const supabaseSupported = useStore(supabaseStore.supported);
  const permissions = useStore(projectStore.permissions);
  const supabaseProject = useStore(supabaseStore.supabaseProject);
  const supabaseIntegration = useStore(supabaseStore.supabaseProjectIntegration);
  const projectId = useStore(projectStore.projectId);

  const handleSupabaseClick = useCallback(() => {
    if (!isSupabaseConfigured) {
      supabaseStore.showSupabaseProjectConfiguration.set(true);
    } else {
      supabaseStore.isConfigVisible.set(true);
    }
  }, [isSupabaseConfigured]);

  const onClickGoToSupabase = useCallback(() => {
    if (import.meta.env.DEV && isUsingSupabaseNative) {
      throw new Error('Dashboard is not supported for the native integration!');
    }

    invariant(supabaseIntegration);

    const {
      project: { id: supabaseProjectId },
    } = supabaseIntegration;

    window.open(`https://supabase.com/dashboard/project/${supabaseProjectId}`);
  }, [supabaseIntegration]);

  const onDisconnect = useCallback(async () => {
    invariant(projectId);

    supabaseStore.supabaseProjectIntegration.set(undefined);
    supabaseStore.supabaseStatus.set('NOT_CONFIGURED');
    supabaseStore.isConfigVisible.set(false);

    await deleteSupabaseProjectIntegration(projectId);

    setIsDisconnectDialogOpen(false);
  }, [projectId]);

  const dialog = (
    <Dialog open={isDisconnectDialogOpen} maxWidth={500} onClose={() => setIsDisconnectDialogOpen(false)}>
      <DialogTitle>Disconnecting project</DialogTitle>
      <DialogDescription>Are you sure you want to disconnect your project?</DialogDescription>
      <DialogActions>
        <DialogButton variant="secondary" onClick={() => setIsDisconnectDialogOpen(false)}>
          Cancel
        </DialogButton>
        <DialogButton variant="warn" onClick={onDisconnect}>
          Disconnect
        </DialogButton>
      </DialogActions>
    </Dialog>
  );

  if (!showIntegrations || !permissions.canEdit) {
    return { item: null, dialog };
  }

  // only show if supported and not unknown status
  if (!supabaseSupported || supabaseStatus === 'UNKNOWN') {
    return { item: null, dialog };
  }

  // only show if not using native or if configured (mirrors SupabaseButton logic)
  const shouldShow = !isUsingSupabaseNative || isSupabaseConfigured;

  if (!shouldShow) {
    return { item: null, dialog };
  }

  // if not configured, show simple integration item
  if (!isSupabaseConfigured) {
    return {
      item: {
        label: (
          <div className="flex items-center gap-2">
            <SupabaseLogo size="xs" />
            <div className="flex flex-col items-start">
              <span className="text-sm font-medium">Supabase</span>
              <span className="text-xs text-bolt-elements-text-secondary">Add a database/edge functions</span>
            </div>
          </div>
        ),
        value: 'supabase-integration',
        onClick: handleSupabaseClick,
      },
      dialog,
    };
  }

  // if configured, show submenu with management options
  return {
    item: {
      label: (
        <div className="flex items-center gap-2">
          <SupabaseLogo size="xs" />
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium">Supabase</span>
            <span className="text-xs text-bolt-elements-text-secondary">Manage your Supabase integration</span>
          </div>
        </div>
      ),
      value: 'supabase-integration',
      submenu: [
        {
          label: (
            <div className="flex flex-col gap-0.5 rounded-md bg-bolt-elements-background-depth-4 px-3 py-2 w-full">
              <div className="flex items-center justify-between">
                <div className="w-40">
                  <div className="truncate text-sm font-medium">{supabaseProject?.name}</div>
                  <span className="text-xs text-bolt-elements-text-secondary">{supabaseProject?.region}</span>
                </div>
              </div>
            </div>
          ),
          value: 'supabase-project-info',
        },
        {
          type: 'divider' as const,
          label: '',
          value: 'divider-1',
        },
        {
          label: (
            <div className="flex items-center gap-2">
              <span className="i-logos:supabase-icon w-4 h-4" />
              <span>Go to Supabase</span>
              <span className="i-ph:arrow-up-right w-4 h-4 ml-auto" />
            </div>
          ),
          value: 'go-to-supabase',
          onClick: onClickGoToSupabase,
        },
        {
          type: 'divider' as const,
          label: '',
          value: 'divider-2',
        },
        {
          label: (
            <div className="flex items-center gap-2">
              <span className="i-ph:plugs w-4 h-4" />
              <span>Disconnect project</span>
            </div>
          ),
          value: 'disconnect-supabase',
          type: 'danger' as const,
          onClick: () => setIsDisconnectDialogOpen(true),
        },
      ],
    },
    dialog,
  };
}
